import { Canceler } from 'axios';
import { action, computed, makeObservable, observable } from 'mobx';
import { PER_PAGE } from 'src/constants/main';
import { modalStore } from 'src/mobx/modalStore';
import client from 'src/services/client';
import { ICategoriesWithProducts, ICategoriesWithProductsCategory, IProductV4 } from 'src/types/product';
import { arrayMerge } from 'src/utils/formatter';
import { getCancelToken, getTranslate } from 'src/utils/helpers';
import logger from '~/services/logger';
import { ICrumb } from '~/view/components/Controls/Crumbs';

export class CrossSellingModalStore {
  constructor() {
    makeObservable(this);
  }

  cancel: Canceler | null = null;

  @observable loading: boolean = false;
  @observable categories: ICategoriesWithProductsCategory[] = [];
  @observable activeCategory: ICategoriesWithProductsCategory | null = null;
  @observable selectedItems: IProductV4[] = [];

  @computed get categoryCrumbs(): ICrumb[] {
    if (!this.categories.length) return [];

    return this.categories.reduce((sum: ICrumb[], current) => {
      if (!current.items?.data.length) return sum;

      return sum.concat(this.transformToCrumb(current));
    }, []);
  }
  private transformToCrumb = (category: ICategoriesWithProductsCategory): ICrumb => {
    const counter: number = category.items.data.filter((item) => this.selectedItems.some((_item) => item.id === _item.id)).length;

    return {
      value: category.id,
      label: getTranslate(category.name),
      counter,
    };
  };

  @action fetchCategories = async () => {
    try {
      if (this.cancel) this.cancel('Cancelled');
      this.loading = true;
      const response: ICategoriesWithProducts = await client.get(`/v4/categories/products`, {
        params: { page: 1, per_page: PER_PAGE.MAXIMUM, is_delete: 0, status: 1 },
        cancelToken: getCancelToken((c) => (this.cancel = c)),
      });

      let allAvailableItems: IProductV4[] = [];

      const categories: ICategoriesWithProductsCategory[] = response.data.reduce((sum: ICategoriesWithProductsCategory[], current) => {
        const availableItems: IProductV4[] = current.items.data.filter((item) => item.id !== modalStore.crossSellingModal?.itemId && !item.type);

        if (!availableItems.length) return sum;

        allAvailableItems = allAvailableItems.concat(availableItems);
        return sum.concat({ ...current, items: { ...current.items, data: availableItems } });
      }, []);

      const selectedItems: IProductV4[] = allAvailableItems.reduce((sum: IProductV4[], current) => {
        return modalStore.crossSellingModal?.selectedItems.some((item) => item.id === current.id) ? sum.concat(current) : sum;
      }, []);

      this.categories = categories;
      this.activeCategory = categories.length ? categories[0] : null;
      this.selectedItems = selectedItems;
      this.loading = false;
    } catch (error: any) {
      if (error.message === 'Cancelled') return;
      this.loading = false;
      logger.error({ error, place: 'CrossSellingModalStore fetchCategories', showModal: true });
    }
  };

  @action selectCategory = (id: number) => {
    this.activeCategory = this.categories.find((category) => category.id === id) || null;
  };

  @action selectItem = (id: number) => {
    const alreadySelect: boolean = this.selectedItems.some((item) => item.id === id);

    if (alreadySelect) {
      this.selectedItems = this.selectedItems.filter((item) => item.id !== id);
    } else {
      const selectedItem: IProductV4 | undefined = this.activeCategory?.items?.data.find((item) => item.id === id);
      if (selectedItem) this.selectedItems = this.selectedItems.concat(selectedItem);
    }
  };

  @action selectAll = () => {
    if (!this.activeCategory?.items?.data?.length) return;

    const activeCategoryItems: IProductV4[] = this.activeCategory?.items.data.filter((item) => !item.out_of_stock);
    this.selectedItems = [...arrayMerge(this.selectedItems, activeCategoryItems, 'id')];
  };

  @action deselectAll = () => {
    this.selectedItems = this.selectedItems.filter((item) => item.category_id !== this.activeCategory?.id);
  };

  @action clickSave = () => {
    modalStore.crossSellingModal?.onSave(this.selectedItems);
    this.closeModal();
  };

  @action closeModal = () => {
    modalStore.closeCrossSellingModal();
    this.clearStore();
  };
  private clearStore = () => {
    this.loading = false;
    this.categories = [];
    this.activeCategory = null;
    this.selectedItems = [];
    if (this.cancel) this.cancel('Cancelled');
  };
}

export const crossSellingModalStore = new CrossSellingModalStore();
